import { Router } from "express";

import { progressReportController } from "../controllers/progress-report-controller";
import { quizReportController } from "../controllers/quiz-report-controller";
import { studentController } from "../controllers/student-controller";
import {
  authenticate,
  requireEmailVerification,
} from "../middleware/auth-middleware";
import { requireRole } from "../middleware/role-middleware";
import {
  attachSchoolContext,
  requireSchoolContext,
} from "../middleware/school-context-middleware";

const router = Router();

// Apply authentication and school role requirement to all routes
router.use(authenticate);
router.use(requireEmailVerification);
router.use(requireRole(["school"]));
router.use(attachSchoolContext);
router.use(requireSchoolContext);

/**
 * STUDENT MANAGEMENT ROUTES (School Admin)
 */

/**
 * @route   GET /api/v1/school/students
 * @desc    Get all students for this specific school with filtering and pagination
 * @access  Private (School Admin)
 * @query   status, grade, academicYear, search, page, limit, sortBy, sortOrder
 */
router.get("/", (req, res) => studentController.getAllSchoolStudents(req, res));

/**
 * @route   GET /api/v1/school/students/statistics
 * @desc    Get comprehensive statistics for students in this school
 * @access  Private (School Admin)
 */
router.get("/statistics", (req, res) =>
  studentController.getStudentStatistics(req, res),
);

/**
 * @route   GET /api/v1/school/students/:id
 * @desc    Get student by ID with full details
 * @access  Private (School Admin)
 */
router.get("/:id", (req, res) => studentController.getStudentById(req, res));

/**
 * @route   PUT /api/v1/school/students/:id
 * @desc    Update student details
 * @access  Private (School Admin)
 */
router.put("/:id", (req, res) => studentController.updateStudent(req, res));

/**
 * @route   PATCH /api/v1/school/students/:id
 * @desc    Update student approval status
 * @access  Private (School Admin)
 * @body    { isApproved: boolean, reason?: string }
 */
router.patch("/:id", (req, res) =>
  studentController.updateStudentApprovalStatus(req, res),
);

/**
 * STUDENT APPROVAL ROUTES
 */

/**
 * @route   PUT /api/v1/school/students/:id/approve
 * @desc    Approve student
 * @access  Private (School Admin)
 */
router.put("/:id/approve", (req, res) =>
  studentController.approveStudent(req, res),
);

/**
 * @route   PUT /api/v1/school/students/:id/reject
 * @desc    Reject student with reason
 * @access  Private (School Admin)
 * @body    { reason: string }
 */
router.put("/:id/reject", (req, res) =>
  studentController.rejectStudent(req, res),
);

/**
 * STUDENT CLASS MANAGEMENT ROUTES
 */

/**
 * @route   GET /api/v1/school/students/:id/classes
 * @desc    Get student's enrolled classes
 * @access  Private (School Admin)
 * @query   status, semesterId, page, limit
 */
router.get("/:id/classes", (req, res) =>
  studentController.getStudentClasses(req, res),
);

/**
 * @route   GET /api/v1/school/students/:id/progress-report
 * @desc    Get student's complete progress report (Teacher/School Admin view)
 * @access  Private (School Admin, Teacher)
 * @query   classId, semesterId
 */
router.get("/:id/progress-report", (req, res) =>
  progressReportController.getStudentProgressReportForTeacher(req, res),
);

/**
 * @route   GET /api/v1/school/students/:studentId/quiz-report
 * @desc    Get quiz report for a specific student (all quizzes)
 * @access  Private (School Admin, Teacher)
 * @query   classId, semesterId
 */
router.get("/:studentId/quiz-report", (req, res) =>
  quizReportController.getStudentQuizReport(req, res),
);

export { router as studentRoutes };
